import { PRICING } from '~/data/site';
import type { MetaDescriptor } from "react-router";
import { SITE } from "~/data/site";
import { getPageSchema } from "~/data/page-schemas";
import { OG_MANIFEST } from "~/data/og-manifest";
import { CANONICALS } from "~/data/canonicals";

interface PageMetaInput {
  title: string; // 50-65 chars
  description: string; // 150-160 chars
  path: string; // e.g., "/", "/about", "/residential"
  ogImage?: string; // defaults to per-path OG image or og-default.jpg (absolute URL)
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | undefined; // optional structured data (single object or array)
  /**
   * Company name for the page title suffix and og:site_name. Defaults to
   * SITE.name (New Leaf) — pass the resolved tenant's name (see
   * `brandingFromMatches` in ~/lib/branding) on any route that renders on
   * more than one tenant's domain, or the title/OG tag names New Leaf on
   * every tenant's page regardless of who actually owns it.
   */
  siteName?: string;
  /**
   * The real origin this request is being served on (branding.origin from
   * `useBranding()`/`brandingFromMatches()`). Defaults to SITE.url (New
   * Leaf's own domain) for callers that predate this field.
   *
   * Canonical/og:url MUST name the domain actually being served — pointing
   * them at nlcstpete.com from a page rendered on demo.broomstack.app is not
   * a cosmetic branding slip, it is a wrong canonical: a crawler can attribute
   * the content to New Leaf's domain instead of indexing the real page.
   */
  origin?: string;
}

/**
 * Determine OG image for a route path.
 * Uses OG_MANIFEST for custom per-page images, falls back to og-default.jpg.
 */
export function ogImageFor(path: string): string {
  const slug = OG_MANIFEST[path];
  return slug
    ? `${SITE.url}/og/${slug}.png`
    : `${SITE.url}/images/og-default.jpg`;
}

export function buildMeta(input: PageMetaInput): MetaDescriptor[] {
  const {
    title,
    description,
    path,
    jsonLd,
    siteName = SITE.name,
    origin = SITE.url,
  } = input;

  // No tenant-specific OG image exists — showing New Leaf's photo as the
  // link-preview image on another company's page is a brand-safety leak,
  // not just a cosmetic one, so it is only ever the default when this really
  // IS New Leaf's own origin. Caller-supplied `ogImage` always wins.
  const isNlcOrigin = origin === SITE.url;
  const ogImage = input.ogImage ?? (isNlcOrigin ? ogImageFor(path) : undefined);

  const canonicalPath = CANONICALS[path] ?? path;
  const canonicalUrl = `${origin}${canonicalPath}`;
  const fulltitle = `${title} | ${siteName}`;

  const descriptors: MetaDescriptor[] = [
    { title: fulltitle },
    { name: "description", content: description },
    { name: "robots", content: "index, follow" },
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1.0",
    },

    // Canonical
    { tagName: "link", rel: "canonical", href: canonicalUrl },

    // Open Graph
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    ...(ogImage
      ? [
          { property: "og:image", content: ogImage },
          { property: "og:image:width", content: "1200" },
          { property: "og:image:height", content: "630" },
        ]
      : []),
    { property: "og:url", content: canonicalUrl },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: siteName },

    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    ...(ogImage ? [{ name: "twitter:image", content: ogImage }] : []),
  ];

  // Add JSON-LD if provided
  if (jsonLd) {
    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    for (const schema of schemas) {
      descriptors.push({
        "script:ld+json": schema,
      } as MetaDescriptor);
    }
  }

  return descriptors;
}

// ---------------------------------------------------------------------------
// Shared address object (DRY across all schema generators)
// ---------------------------------------------------------------------------
const NLC_ADDRESS = {
  "@type": "PostalAddress" as const,
  addressLocality: "St. Petersburg",
  addressRegion: "FL",
  postalCode: "33713",
  addressCountry: "US",
};

// ---------------------------------------------------------------------------
// Build areaServed from the full Tampa Bay service area
// ---------------------------------------------------------------------------
function buildAreaServed() {
  return [
    "St. Petersburg", "Tampa", "Clearwater", "Largo", "Dunedin",
    "Palm Harbor", "Safety Harbor", "Seminole", "Bradenton", "Sarasota",
  ].map((name) => ({ "@type": "City" as const, name }));
}

/**
 * Comprehensive LocalBusiness + CleaningService schema.
 * Rendered on EVERY public page via root.tsx.
 */
export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "CleaningService"],
    "@id": `${SITE.url}/#business`,
    name: SITE.name,
    image: `${SITE.url}/images/og-default.jpg`,
    founder: { "@type": "Person", name: "Vikelas Carey" },
    foundingDate: "2025-12",
    description:
      "Professional house cleaning for Tampa Bay homes. W-2 employees, eco-friendly products, transparent sq-ft pricing.",
    url: SITE.url,
    email: SITE.contact.email,
    address: NLC_ADDRESS,
    openingHours: "Mo-Sa 07:00-18:00",
    priceRange: `$${PRICING.weeklyRate.toFixed(2)}-$${PRICING.moveOutRate.toFixed(2)}/sq ft`,
    areaServed: buildAreaServed(),
    sameAs: [
      SITE.social.yelp,
      SITE.social.facebook,
      SITE.social.instagram,
      SITE.social.google,
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: SITE.reviews.yelpCount,
      bestRating: "5",
    },
  };
}

/**
 * BreadcrumbList schema (used on all non-homepage pages)
 */
export function breadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * FAQPage schema (used on service and city pages)
 */
export function faqPageSchema(
  faqs: Array<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/**
 * Aggregate rating schema. Uses values from SITE.reviews — never invented.
 * Attach to the reviews page meta JSON-LD.
 */
export function aggregateRatingSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    itemReviewed: {
      "@type": "LocalBusiness",
      name: SITE.name,
      "@id": `${SITE.url}/#business`,
    },
    ratingValue: SITE.reviews.yelpRating,
    reviewCount: SITE.reviews.yelpCount,
    bestRating: "5",
    worstRating: "1",
  };
}

interface Review {
  reviewerName: string;
  rating: number;
  date: string;
  text: string;
  source: string;
}

/**
 * Review schema for a single user review.
 */
export function reviewSchema(review: Review) {
  return {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "LocalBusiness",
      name: SITE.name,
      "@id": `${SITE.url}/#business`,
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: String(review.rating),
      bestRating: "5",
      worstRating: "1",
    },
    author: {
      "@type": "Person",
      name: review.reviewerName,
    },
    datePublished: review.date,
    reviewBody: review.text,
    publisher: {
      "@type": "Organization",
      name: review.source.charAt(0).toUpperCase() + review.source.slice(1),
    },
  };
}

/**
 * Build a single JSON-LD payload (@graph) containing breadcrumbs,
 * AggregateRating, and one Review node per review. Used by the Reviews page.
 */
export function reviewsPageSchema(
  reviews: Review[],
  breadcrumbs: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbSchema(breadcrumbs),
      aggregateRatingSchema(),
      ...reviews.map((r) => reviewSchema(r)),
    ],
  };
}

/**
 * Service schema entries for the residential services page.
 * Returns one Service block per cleaning type.
 */
export function residentialServiceSchemas() {
  const provider = {
    "@type": "LocalBusiness" as const,
    name: SITE.name,
    "@id": `${SITE.url}/#business`,
  };
  const areaServed = buildAreaServed();

  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Recurring Maintenance Cleaning",
      provider,
      areaServed,
      description:
        "Weekly, biweekly, or monthly eco-friendly house cleaning with W-2 employees, consistent crew assignment, and published per-sq-ft pricing.",
      offers: {
        "@type": "Offer",
        price: PRICING.weeklyRate.toFixed(2),
        priceCurrency: "USD",
        unitText: "per square foot",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Deep Clean",
      provider,
      areaServed,
      description:
        "Thorough top-to-bottom deep cleaning. Required first visit for new recurring clients, or standalone on-demand.",
      offers: {
        "@type": "Offer",
        price: PRICING.fullDeepRate.toFixed(2),
        priceCurrency: "USD",
        unitText: "per square foot",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Move-In / Move-Out Cleaning",
      provider,
      areaServed,
      description:
        "Unfurnished home cleaned top-to-bottom at a flat per-sq-ft rate. Walls excluded.",
      offers: {
        "@type": "Offer",
        price: PRICING.moveOutRate.toFixed(2),
        priceCurrency: "USD",
        unitText: "per square foot",
      },
    },
  ];
}

/**
 * Service schema for a single service (used by individual service pages).
 */
function serviceSchema(
  name: string,
  description: string,
  pricePerUnit: string,
  unitText: string,
) {
  const provider = {
    "@type": "LocalBusiness" as const,
    name: SITE.name,
    "@id": `${SITE.url}/#business`,
  };
  const areaServed = buildAreaServed();

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    provider,
    areaServed,
    description,
    offers: {
      "@type": "Offer",
      price: pricePerUnit,
      priceCurrency: "USD",
      unitText,
    },
  };
}

/**
 * Generate page-appropriate JSON-LD based on the path.
 * Returns a @graph containing Service, AggregateRating, etc. as configured in PAGE_SCHEMAS.
 * If the path has no special schema, returns undefined (no additional schema needed).
 */
export function schemaFor(
  path: string,
  /**
   * True on New Leaf's own domain. Default `true` for the many existing
   * callers that predate tenant-aware pages — changing their behavior is out
   * of scope here; only callers that pass this explicitly get the guard.
   *
   * `ratingValue`/`reviewCount` on AggregateRating are New Leaf's real Yelp
   * numbers (SITE.reviews) — asserting them as another company's rating on
   * its own domain is fabricated review data, not a cosmetic leak, so this
   * is the one piece of `schemaFor`'s output that never degrades to "close
   * enough": it is omitted entirely rather than shown wrong.
   */
  isNewLeaf = true,
): Record<string, unknown> | Record<string, unknown>[] | undefined {
  const pageConfig = getPageSchema(path);
  const includeRating = pageConfig.includeRating && isNewLeaf;

  // If no special schema is configured for this path, return undefined
  // (buildMeta will skip jsonLd if undefined)
  if (!pageConfig.service && !includeRating && !pageConfig.faqs?.length) {
    return undefined;
  }

  // Build array of schema blocks to include
  const schemas: Record<string, unknown>[] = [];

  // Add Service schema if configured
  if (pageConfig.service) {
    schemas.push(
      serviceSchema(
        pageConfig.service.name,
        pageConfig.service.description,
        pageConfig.service.pricePerUnit,
        pageConfig.service.unitText,
      ),
    );
  }

  // Add FAQ schema if configured
  if (pageConfig.faqs?.length) {
    schemas.push(faqPageSchema(pageConfig.faqs));
  }

  // Add AggregateRating if configured — New Leaf's own domain only (see above).
  if (includeRating) {
    schemas.push(aggregateRatingSchema());
  }

  // If only one schema, return it directly; if multiple, wrap in @graph; if none, return undefined
  if (schemas.length === 1) {
    return schemas[0];
  }

  if (schemas.length > 1) {
    return {
      "@context": "https://schema.org",
      "@graph": schemas,
    };
  }

  return undefined;
}
